<template>
  <v-col class="filters-content">
    <!-- Header -->
    <v-row class="mb-4">
      <v-col>
        <h1 class="headline font-weight-bold mb-2">
          Filters Guide
        </h1>
        <p class="body-1 coolgray--text">
          Learn how to use filters to narrow down data and find exactly what you're looking for.
        </p>
      </v-col>
    </v-row>

    <!-- Overview -->
    <v-row class="mb-6">
      <v-col>
        <v-card
          outlined
          class="pa-4"
        >
          <h2 class="subtitle-1 font-weight-bold mb-3 d-flex align-center">
            <font-awesome-icon
              :icon="['fas', 'filter']"
              class="primary--text mr-2"
            />
            Filter Overview
          </h2>

          <p class="body-2 mb-3">
            Filters help you focus on specific data by narrowing results based on criteria you define.
            Most views in iAdOps support filtering through the Filter toolbar button.
          </p>

          <h3 class="subtitle-2 font-weight-bold mb-2 mt-4">
            Accessing Filters
          </h3>
          <ol class="body-2 mb-4">
            <li class="mb-2">
              <strong>Click the Filter button</strong> in the toolbar to open the filter panel.
            </li>
            <li class="mb-2">
              <strong>Set your criteria</strong> using the available filter controls.
            </li>
            <li class="mb-2">
              <strong>Click Apply</strong> to filter the current view.
            </li>
            <li class="mb-2">
              <strong>Click Clear</strong> to remove all filters and show all data.
            </li>
          </ol>

          <v-alert
            type="info"
            dense
            text
            class="body-2"
          >
            Active filters are indicated by a badge on the Filter button showing the number of filters applied.
          </v-alert>
        </v-card>
      </v-col>
    </v-row>

    <!-- Filter Types -->
    <v-row class="mb-6">
      <v-col>
        <v-card
          outlined
          class="pa-4"
        >
          <h2 class="subtitle-1 font-weight-bold mb-3 d-flex align-center">
            <font-awesome-icon
              :icon="['fas', 'sliders-h']"
              class="info--text mr-2"
            />
            Filter Types
          </h2>

          <p class="body-2 mb-4">
            iAdOps provides several types of filter controls depending on the data being filtered:
          </p>

          <!-- Text Filters -->
          <h3 class="subtitle-2 font-weight-bold mb-2">
            Text Filters
          </h3>
          <v-card
            flat
            :class="`${infoCardClass} pa-3 mb-4`"
          >
            <p class="body-2 mb-2">
              Text filters let you search for values containing specific text.
            </p>
            <ul class="body-2 mb-0">
              <li><strong>Contains:</strong> Match values containing the search text</li>
              <li><strong>Starts with:</strong> Match values beginning with the search text</li>
              <li><strong>Exact match:</strong> Match values exactly equal to the search text</li>
            </ul>
            <p class="body-2 mt-2 mb-0">
              <em>Example:</em> Filter "Advertiser" by "Acme" to show all advertisers containing "Acme" in their name.
            </p>
          </v-card>

          <!-- Select Filters -->
          <h3 class="subtitle-2 font-weight-bold mb-2">
            Select Filters
          </h3>
          <v-card
            flat
            :class="`${infoCardClass} pa-3 mb-4`"
          >
            <p class="body-2 mb-2">
              Select filters let you choose from predefined options.
            </p>
            <ul class="body-2 mb-0">
              <li><strong>Single select:</strong> Choose one value from a dropdown</li>
              <li><strong>Multi-select:</strong> Choose multiple values (results match any selected value)</li>
            </ul>
            <p class="body-2 mt-2 mb-0">
              <em>Example:</em> Filter "Status" to show only "Delivering" or "Ready" line items.
            </p>
          </v-card>

          <!-- Numeric Filters -->
          <h3 class="subtitle-2 font-weight-bold mb-2">
            Numeric Filters
          </h3>
          <v-card
            flat
            :class="`${infoCardClass} pa-3 mb-4`"
          >
            <p class="body-2 mb-2">
              Numeric filters let you filter by number ranges or comparisons.
            </p>
            <ul class="body-2 mb-0">
              <li><strong>Greater than:</strong> Values above the specified number</li>
              <li><strong>Less than:</strong> Values below the specified number</li>
              <li><strong>Between:</strong> Values within a range (min to max)</li>
              <li><strong>Equals:</strong> Exact numeric match</li>
            </ul>
            <p class="body-2 mt-2 mb-0">
              <em>Example:</em> Filter "Pacing %" greater than 120 to find over-delivering campaigns.
            </p>
          </v-card>

          <!-- Date Filters -->
          <h3 class="subtitle-2 font-weight-bold mb-2">
            Date Filters
          </h3>
          <v-card
            flat
            :class="`${infoCardClass} pa-3 mb-4`"
          >
            <p class="body-2 mb-2">
              Date filters let you filter by date ranges.
            </p>
            <ul class="body-2 mb-0">
              <li><strong>Start date:</strong> Records on or after this date</li>
              <li><strong>End date:</strong> Records on or before this date</li>
              <li><strong>Date range:</strong> Records within a specific period</li>
            </ul>
            <p class="body-2 mt-2 mb-0">
              <em>Example:</em> Filter campaigns ending this week to focus on urgent delivery issues.
            </p>
          </v-card>

          <!-- Boolean Filters -->
          <h3 class="subtitle-2 font-weight-bold mb-2">
            Boolean Filters
          </h3>
          <v-card
            flat
            :class="`${infoCardClass} pa-3 mb-4`"
          >
            <p class="body-2 mb-2">
              Boolean filters let you toggle yes/no conditions.
            </p>
            <ul class="body-2 mb-0">
              <li><strong>Yes:</strong> Show records where the condition is true</li>
              <li><strong>No:</strong> Show records where the condition is false</li>
              <li><strong>Any:</strong> Don't filter by this condition</li>
            </ul>
            <p class="body-2 mt-2 mb-0">
              <em>Example:</em> Filter "Missing Creatives" = Yes to find line items that need creative uploads.
            </p>
          </v-card>
        </v-card>
      </v-col>
    </v-row>

    <!-- Delivery Filters -->
    <v-row class="mb-6">
      <v-col>
        <v-card
          outlined
          class="pa-4"
        >
          <h2 class="subtitle-1 font-weight-bold mb-3 d-flex align-center">
            <font-awesome-icon
              :icon="['fas', 'chart-line']"
              class="primary--text mr-2"
            />
            Delivery Report Filters
          </h2>

          <h3 class="subtitle-2 font-weight-bold mb-2">
            Pacing Filters
          </h3>
          <p class="body-2 mb-3">
            The following filters are available in the Pacing report:
          </p>

          <v-simple-table
            dense
            class="mb-4"
          >
            <thead>
              <tr>
                <th class="text-left">
                  Filter
                </th>
                <th class="text-left">
                  Type
                </th>
                <th class="text-left">
                  Description
                </th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  <font-awesome-icon
                    :icon="['fas', 'circle-check']"
                    class="mr-1"
                    size="sm"
                  />
                  Status
                </td>
                <td>Multi-Select</td>
                <td>Filter by delivery status (e.g., delivering, paused, completed)</td>
              </tr>
              <tr>
                <td>
                  <font-awesome-icon
                    :icon="['fas', 'building']"
                    class="mr-1"
                    size="sm"
                  />
                  Advertiser
                </td>
                <td>Multi-Select</td>
                <td>Filter by advertiser name</td>
              </tr>
              <tr>
                <td>
                  <font-awesome-icon
                    :icon="['fas', 'file-lines']"
                    class="mr-1"
                    size="sm"
                  />
                  Order
                </td>
                <td>Multi-Select</td>
                <td>Filter by order name</td>
              </tr>
              <tr>
                <td>
                  <font-awesome-icon
                    :icon="['fas', 'arrow-up-1-9']"
                    class="mr-1"
                    size="sm"
                  />
                  Priority
                </td>
                <td>Multi-Select</td>
                <td>Filter by line item priority level</td>
              </tr>
              <tr>
                <td>
                  <font-awesome-icon
                    :icon="['fas', 'user-tie']"
                    class="mr-1"
                    size="sm"
                  />
                  Salesperson
                </td>
                <td>Multi-Select</td>
                <td>Filter by assigned salesperson</td>
              </tr>
              <tr>
                <td>
                  <font-awesome-icon
                    :icon="['fas', 'user']"
                    class="mr-1"
                    size="sm"
                  />
                  Trafficker
                </td>
                <td>Multi-Select</td>
                <td>Filter by assigned trafficker</td>
              </tr>
              <tr>
                <td>
                  <font-awesome-icon
                    :icon="['fas', 'square-xmark']"
                    class="mr-1"
                    size="sm"
                  />
                  Missing Creatives
                </td>
                <td>Yes/No</td>
                <td>Filter line items that are missing creative assets</td>
              </tr>
            </tbody>
          </v-simple-table>

          <h3 class="subtitle-2 font-weight-bold mb-2 mt-4">
            Discrepancy Filters
          </h3>
          <p class="body-2 mb-3">
            The following filters are available in the Discrepancy report:
          </p>

          <v-simple-table
            dense
            class="mb-4"
          >
            <thead>
              <tr>
                <th class="text-left">
                  Filter
                </th>
                <th class="text-left">
                  Type
                </th>
                <th class="text-left">
                  Description
                </th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  <font-awesome-icon
                    :icon="['fas', 'building']"
                    class="mr-1"
                    size="sm"
                  />
                  Advertiser
                </td>
                <td>Multi-Select</td>
                <td>Filter by advertiser name</td>
              </tr>
              <tr>
                <td>
                  <font-awesome-icon
                    :icon="['fas', 'file-lines']"
                    class="mr-1"
                    size="sm"
                  />
                  Order
                </td>
                <td>Multi-Select</td>
                <td>Filter by order name</td>
              </tr>
              <tr>
                <td>
                  <font-awesome-icon
                    :icon="['fas', 'arrow-up-1-9']"
                    class="mr-1"
                    size="sm"
                  />
                  Priority
                </td>
                <td>Multi-Select</td>
                <td>Filter by GAM priority level</td>
              </tr>
              <tr>
                <td>
                  <font-awesome-icon
                    :icon="['fas', 'user-tie']"
                    class="mr-1"
                    size="sm"
                  />
                  Salesperson
                </td>
                <td>Multi-Select</td>
                <td>Filter by assigned salesperson</td>
              </tr>
              <tr>
                <td>
                  <font-awesome-icon
                    :icon="['fas', 'user']"
                    class="mr-1"
                    size="sm"
                  />
                  Trafficker
                </td>
                <td>Multi-Select</td>
                <td>Filter by assigned trafficker</td>
              </tr>
              <tr>
                <td>
                  <font-awesome-icon
                    :icon="['fas', 'square-xmark']"
                    class="mr-1"
                    size="sm"
                  />
                  Missing Creatives
                </td>
                <td>Yes/No</td>
                <td>Filter line items that are missing creative assets</td>
              </tr>
            </tbody>
          </v-simple-table>

          <v-card
            :class="`${infoCardClass} pa-3`"
          >
            <p class="body-2 mb-0">
              <strong>Tip:</strong> All delivery filters support multi-select. Hold
              <kbd>Ctrl</kbd> (Windows) or <kbd>Cmd</kbd> (Mac) to select multiple values.
              The filter panel shows a count of active filters when applied.
            </p>
          </v-card>
        </v-card>
      </v-col>
    </v-row>

    <!-- Pacing Saved Filters -->
    <v-row class="mb-6">
      <v-col>
        <v-card
          outlined
          class="pa-4"
        >
          <h2 class="subtitle-1 font-weight-bold mb-3 d-flex align-center">
            <font-awesome-icon
              :icon="['fas', 'bookmark']"
              class="warning--text mr-2"
            />
            Pacing Saved Filters
          </h2>

          <p class="body-2 mb-4">
            Pre-configured filters appear as tabs at the top of the Pacing view.
            Click any saved filter to instantly see line items matching that criteria.
          </p>

          <v-simple-table
            dense
            class="mb-4"
          >
            <thead>
              <tr>
                <th class="text-left">
                  Saved Filter
                </th>
                <th class="text-left">
                  Criteria
                </th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td class="font-weight-medium">
                  <font-awesome-icon
                    :icon="['fas', 'exclamation-triangle']"
                    class="warning--text mr-1"
                    size="sm"
                  />
                  At Risk
                </td>
                <td>
                  Line items that are delivering impressions AND ending within the next 14 days
                  AND have an OSI less than 100. These may not deliver their full impression goal.
                </td>
              </tr>
              <tr>
                <td class="font-weight-medium">
                  <font-awesome-icon
                    :icon="['fas', 'pause-circle']"
                    class="error--text mr-1"
                    size="sm"
                  />
                  Stuck in Paused
                </td>
                <td>
                  Line items that started within the last 14 days but whose status remains PAUSED.
                </td>
              </tr>
              <tr>
                <td class="font-weight-medium">
                  <font-awesome-icon
                    :icon="['fas', 'clock']"
                    class="info--text mr-1"
                    size="sm"
                  />
                  Stuck in Ready
                </td>
                <td>
                  Line items that started within the last 14 days but whose status remains READY.
                </td>
              </tr>
              <tr>
                <td class="font-weight-medium">
                  <font-awesome-icon
                    :icon="['fas', 'image']"
                    class="error--text mr-1"
                    size="sm"
                  />
                  Missing Creatives
                </td>
                <td>
                  Line items currently live or set to go live in the next 14 days that are
                  missing at least 1 creative asset.
                </td>
              </tr>
              <tr>
                <td class="font-weight-medium">
                  <font-awesome-icon
                    :icon="['fas', 'chart-line']"
                    class="coolgray--text mr-1"
                    size="sm"
                  />
                  Zero Imps Yesterday
                </td>
                <td>
                  Line items with status DELIVERING that delivered zero impressions yesterday.
                  Excludes click-tracking-only line items.
                </td>
              </tr>
              <tr>
                <td class="font-weight-medium">
                  <font-awesome-icon
                    :icon="['fas', 'calendar-times']"
                    class="primary--text mr-1"
                    size="sm"
                  />
                  Ending Soon
                </td>
                <td>
                  Currently delivering line items ending within the next 3 days, OR line items
                  with DELIVERING status and end dates within the last 4 days.
                </td>
              </tr>
            </tbody>
          </v-simple-table>

          <h3 class="subtitle-2 font-weight-bold mb-2 mt-4">
            Understanding OSI (On Schedule Indicator)
          </h3>
          <v-card
            :class="`${infoCardClass} pa-3`"
          >
            <p class="body-2 mb-2">
              <strong>OSI</strong> is a straight-line calculation that predicts whether a line item
              will deliver its full impression goal by the end date.
            </p>
            <ul class="body-2 mb-0">
              <li><strong>OSI = 100:</strong> Line item is on track to deliver 100% of its goal</li>
              <li><strong>OSI &lt; 100:</strong> Under-delivering; may not reach goal</li>
              <li><strong>OSI &gt; 100:</strong> Over-delivering; will exceed goal</li>
            </ul>
            <p class="body-2 mt-2 mb-0">
              <em>Formula:</em> (Average Daily Impressions × Days Remaining) ÷ Remaining Goal
            </p>
          </v-card>
        </v-card>
      </v-col>
    </v-row>

    <!-- Internal Filters -->
    <v-row
      v-if="showInternalContent"
      class="mb-6"
    >
      <v-col>
        <v-card
          outlined
          class="pa-4"
        >
          <h2 class="subtitle-1 font-weight-bold mb-3 d-flex align-center">
            <font-awesome-icon
              :icon="['fas', 'users-cog']"
              class="success--text mr-2"
            />
            Internal View Filters
          </h2>

          <p class="body-2 mb-4">
            Filters available in Organization, Project, People, and Time views:
          </p>

          <v-simple-table
            dense
            class="mb-4"
          >
            <thead>
              <tr>
                <th class="text-left">
                  Filter
                </th>
                <th class="text-left">
                  Available In
                </th>
                <th class="text-left">
                  Description
                </th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Client Name</td>
                <td>Organizations, Projects</td>
                <td>Filter by client/organization name</td>
              </tr>
              <tr>
                <td>Project Name</td>
                <td>Projects, Time</td>
                <td>Filter by project name</td>
              </tr>
              <tr>
                <td>Person Name</td>
                <td>People, Time</td>
                <td>Filter by team member name</td>
              </tr>
              <tr>
                <td>Time Classification</td>
                <td>Time</td>
                <td>Filter by time entry type (billable, internal, etc.)</td>
              </tr>
              <tr>
                <td>Status</td>
                <td>Organizations, Projects</td>
                <td>Filter by active/inactive status</td>
              </tr>
              <tr>
                <td>Date Range</td>
                <td>Time</td>
                <td>Filter time entries by date</td>
              </tr>
            </tbody>
          </v-simple-table>
        </v-card>
      </v-col>
    </v-row>

    <!-- Grouping -->
    <v-row class="mb-6">
      <v-col>
        <v-card
          outlined
          class="pa-4"
        >
          <h2 class="subtitle-1 font-weight-bold mb-3 d-flex align-center">
            <font-awesome-icon
              :icon="['fas', 'layer-group']"
              class="warning--text mr-2"
            />
            Grouping Data
          </h2>

          <p class="body-2 mb-3">
            Grouping organizes your data into collapsible sections based on a shared property.
            This is different from filtering&mdash;grouping changes how data is organized, not what data is shown.
          </p>

          <h3 class="subtitle-2 font-weight-bold mb-2 mt-4">
            Using Groups
          </h3>
          <ol class="body-2 mb-4">
            <li class="mb-2">
              <strong>Click the Group button</strong> in the toolbar.
            </li>
            <li class="mb-2">
              <strong>Select grouping properties</strong> (e.g., Advertiser, Order, Client).
            </li>
            <li class="mb-2">
              <strong>Click Apply</strong> to reorganize the data into groups.
            </li>
            <li class="mb-2">
              <strong>Expand/collapse groups</strong> by clicking the group headers.
            </li>
          </ol>

          <v-alert
            type="info"
            dense
            text
            class="body-2"
          >
            Grouping can be combined with filtering. First filter to narrow the data, then group to organize results.
          </v-alert>
        </v-card>
      </v-col>
    </v-row>

    <!-- Search -->
    <v-row class="mb-6">
      <v-col>
        <v-card
          outlined
          class="pa-4"
        >
          <h2 class="subtitle-1 font-weight-bold mb-3 d-flex align-center">
            <font-awesome-icon
              :icon="['fas', 'search']"
              class="secondary--text mr-2"
            />
            Quick Search
          </h2>

          <p class="body-2 mb-3">
            The search box provides instant filtering as you type. Unlike the Filter panel, quick search
            is always visible and searches across multiple columns simultaneously.
          </p>

          <v-card
            flat
            :class="`${infoCardClass} pa-3 mb-4`"
          >
            <p class="body-2 mb-0">
              <strong>Tip:</strong> Quick search is great for finding specific records fast.
              Use the Filter panel when you need more precise control over which columns to search
              and what comparison operators to use.
            </p>
          </v-card>
        </v-card>
      </v-col>
    </v-row>

    <!-- Export -->
    <v-row class="mb-6">
      <v-col>
        <v-card
          outlined
          class="pa-4"
        >
          <h2 class="subtitle-1 font-weight-bold mb-3 d-flex align-center">
            <font-awesome-icon
              :icon="['fas', 'file-export']"
              class="info--text mr-2"
            />
            Exporting Filtered Data
          </h2>

          <p class="body-2 mb-3">
            When you export data, only the currently filtered and visible data is included in the export file.
          </p>

          <ol class="body-2">
            <li class="mb-2">
              <strong>Apply filters</strong> to narrow down the data you want to export.
            </li>
            <li class="mb-2">
              <strong>Configure columns</strong> using the Column picker to choose which columns to include.
            </li>
            <li class="mb-2">
              <strong>Click Export</strong> to download the filtered data as an Excel file.
            </li>
          </ol>
        </v-card>
      </v-col>
    </v-row>
  </v-col>
</template>

<script>
import themeClasses from '@/mixins/themeClasses'

export default {
  name: 'FiltersContent',
  mixins: [themeClasses],
  props: {
    showInternalContent: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    infoCardClass() {
      return this.themeSurfaceColor('grey lighten-4', 'grey darken-3')
    },
  },
}
</script>

<style lang="scss" scoped>
.filters-content {
  max-width: 900px;
}

ol, ul {
  padding-left: 20px;
}
</style>
